import React from 'react';
import { X, Calendar, Search, AlertTriangle, Shield, GitCompareArrows, Link2, FileText, Scale } from 'lucide-react';
import { useAskSidebar, ContextItem } from '../../contexts/AskSidebarContext';

const TYPE_META: Record<ContextItem['type'], { icon: React.FC<{ className?: string }>; color: string; label: string }> = {
  event:            { icon: Calendar,         color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',           label: 'Event' },
  gap:              { icon: Search,           color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',       label: 'Gap' },
  inconsistency:    { icon: AlertTriangle,    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',               label: 'Inconsistency' },
  'standard-of-care': { icon: Shield,         color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', label: 'Standard' },
  comparative:      { icon: GitCompareArrows, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',   label: 'Comparative' },
  causation:        { icon: Link2,            color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',   label: 'Causation' },
  text:             { icon: FileText,         color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',             label: 'Text' },
  'expert-opinion': { icon: Scale,            color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',       label: 'Expert' },
};

export const SelectedContextChips: React.FC = () => {
  const { contextItems, removeFromContext, clearContext, textSelection, setTextSelection } = useAskSidebar();

  if (contextItems.length === 0 && !textSelection) return null;

  return (
    <div className="px-3 py-2.5 border-t border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="font-semibold">Pinned ({contextItems.length + (textSelection ? 1 : 0)})</span>
          <span className="text-[10px] text-purple-500 dark:text-purple-400">&mdash; ask questions about these</span>
        </span>
        <button
          onClick={() => { clearContext(); setTextSelection(null); }}
          className="text-[11px] text-purple-400 hover:text-red-500 dark:text-purple-500 dark:hover:text-red-400 transition-colors font-medium"
        >
          Clear all
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {contextItems.map(item => {
          const meta = TYPE_META[item.type];
          const Icon = meta.icon;
          return (
            <span
              key={item.id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.color}`}
            >
              <Icon className="h-2.5 w-2.5" />
              <span className="max-w-[120px] truncate">{item.summary}</span>
              <button
                onClick={() => removeFromContext(item.id)}
                className="ml-0.5 hover:opacity-70 transition-opacity"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          );
        })}
        {textSelection && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <FileText className="h-2.5 w-2.5" />
            <span className="max-w-[120px] truncate">{textSelection.text}</span>
            <button
              onClick={() => setTextSelection(null)}
              className="ml-0.5 hover:opacity-70 transition-opacity"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        )}
      </div>
    </div>
  );
};
